import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { toast } from "react-toastify";
import { Monitor, Plus } from "react-feather";
import Select from "react-select";
import { pointService } from "../../../services/pointService";
import { pointTypeService } from "../../../services/pointTypeService";
import { projectService } from "../../../services/projectService";
import MdiIconPicker from "../../Common/MdiIconPicker";

/**
 * AssignPointToDeviceModal - Modal for assigning an unassigned point to a device.
 * Manages its own state for point search, project filter, available points, etc.
 * Props:
 *   isOpen: bool
 *   device: the device to assign the point to (assigningPointToDevice)
 *   onClose: () => void
 *   onSuccess: () => void — called after successful assignment to refresh parent
 */
const AssignPointToDeviceModal = ({ isOpen, device, onClose, onSuccess }) => {
  const [availablePoints, setAvailablePoints] = useState([]);
  const [loadingAvailablePoints, setLoadingAvailablePoints] = useState(false);
  const [selectedPointId, setSelectedPointId] = useState("");
  const [assignPointDisplayName, setAssignPointDisplayName] = useState("");
  const [assignPointIcon, setAssignPointIcon] = useState("");
  const [assigningPoint, setAssigningPoint] = useState(false);
  const [pointSearchTerm, setPointSearchTerm] = useState("");
  const [assignPointProjects, setAssignPointProjects] = useState([]);
  const [loadingAssignPointProjects, setLoadingAssignPointProjects] = useState(false);
  const [selectedAssignPointProject, setSelectedAssignPointProject] = useState(null);
  const [pointTypes, setPointTypes] = useState([]);
  const [loadingPointTypes, setLoadingPointTypes] = useState(false);
  const [assignPointTypeId, setAssignPointTypeId] = useState("");

  useEffect(() => {
    if (isOpen && device) {
      setSelectedPointId("");
      setPointSearchTerm("");
      setAssignPointDisplayName("");
      setAssignPointIcon("");
      setSelectedAssignPointProject(null);
      fetchAvailablePoints();
      fetchAssignPointProjects();
      setAssignPointTypeId("");
      fetchPointTypes();
    }
  }, [isOpen, device]);

  const fetchPointTypes = async () => {
    setLoadingPointTypes(true);
    try {
      const response = await pointTypeService.search({ name: "", page: 0, size: 1000 });
      setPointTypes(response.content || []);
    } catch (error) {
      console.error("Error fetching point types:", error);
      toast.error("Failed to load point types");
    } finally {
      setLoadingPointTypes(false);
    }
  };

  const fetchAssignPointProjects = async () => {
    setLoadingAssignPointProjects(true);
    try {
      const response = await projectService.list({ page: 0, size: 1000 });
      const projectOptions = (response.content || []).map((project) => ({
        value: project.projId || project.projName,
        label: `${project.projName} - ${project.recDisplayName || project.deviceId}`,
      }));
      setAssignPointProjects(projectOptions);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setAssignPointProjects([]);
    } finally {
      setLoadingAssignPointProjects(false);
    }
  };

  const fetchAvailablePoints = async (searchTerm = "", projId = "") => {
    setLoadingAvailablePoints(true);
    try {
      const params = {
        page: 0,
        size: 100,
        ...(searchTerm?.trim() && { pointName: searchTerm.trim() }),
        ...(projId && { projId }),
      };
      const response = await pointService.searchUnassigned(params);
      setAvailablePoints(response.content || response || []);
    } catch (error) {
      console.error("Error fetching available points:", error);
      toast.error("Failed to load available points");
      setAvailablePoints([]);
    } finally {
      setLoadingAvailablePoints(false);
    }
  };

  const handlePointSearch = (searchTerm) => {
    setPointSearchTerm(searchTerm);
    fetchAvailablePoints(searchTerm, selectedAssignPointProject?.value || "");
  };

  const handleAssignPointProjectChange = (selectedOption) => {
    setSelectedAssignPointProject(selectedOption);
    fetchAvailablePoints(pointSearchTerm, selectedOption?.value || "");
  };

  const handleConfirmAssignPoint = async () => {
    if (!selectedPointId) {
      toast.error("Please select a point");
      return;
    }
    if (!assignPointDisplayName || !assignPointDisplayName.trim()) {
      toast.error("Please enter a display name");
      return;
    }

    setAssigningPoint(true);
    try {
      const body = {
        deviceId: device.id,
        displayName: assignPointDisplayName.trim(),
        icon: assignPointIcon.trim(),
        ...(assignPointTypeId && { pointTypeId: parseInt(assignPointTypeId) }),
      };

      await pointService.assignDevice(selectedPointId, body);
      toast.success("Point assigned to device successfully");

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error("Error assigning point to device:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to assign point to device");
      }
    } finally {
      setAssigningPoint(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>Assign Point to Device</ModalHeader>
      <ModalBody>
        <div className="mb-3 p-3 bg-light rounded">
          <div className="d-flex align-items-center">
            <Monitor size={20} className="me-2 text-primary" />
            <div>
              <small className="text-muted">Assigning point to device:</small>
              <div className="fw-bold">{device?.name}</div>
              <small className="text-muted">Device ID: {device?.id}</small>
            </div>
          </div>
        </div>

        <FormGroup>
          <Label for="assignPointProject">Filter by Project</Label>
          <Select
            id="assignPointProject"
            options={assignPointProjects}
            value={selectedAssignPointProject}
            onChange={handleAssignPointProjectChange}
            isClearable
            isSearchable
            isLoading={loadingAssignPointProjects}
            isDisabled={assigningPoint}
            placeholder="All projects..."
            styles={{
              control: (base) => ({
                ...base,
                minHeight: "38px",
              }),
            }}
          />
          <small className="text-muted">Select a project to filter points</small>
        </FormGroup>

        <FormGroup>
          <Label for="pointSearch">Search Points</Label>
          <Input
            id="pointSearch"
            type="text"
            placeholder="Search by point name..."
            value={pointSearchTerm}
            onChange={(e) => handlePointSearch(e.target.value)}
            disabled={assigningPoint}
          />
          <small className="text-muted">Type to search for points by name</small>
        </FormGroup>

        <FormGroup>
          <Label for="pointSelect">
            Select Point <span className="text-danger">*</span>
          </Label>
          <Input
            id="pointSelect"
            type="select"
            value={selectedPointId}
            onChange={(e) => setSelectedPointId(e.target.value)}
            disabled={assigningPoint || loadingAvailablePoints}
            size="10"
          >
            <option value="">Select a point...</option>
            {availablePoints.map((point, index) => (
              <option key={index} value={point.pointId}>
                {point.pointId} - {point.pointDisplayName || "No display name"} ({point.kind})
              </option>
            ))}
          </Input>
          {loadingAvailablePoints && (
            <div className="mt-2">
              <span className="spinner-border spinner-border-sm me-2"></span>
              <small className="text-muted">Loading points...</small>
            </div>
          )}
          {!loadingAvailablePoints &&
            availablePoints.length === 0 &&
            (pointSearchTerm || selectedAssignPointProject) && (
              <small className="text-muted">
                No unassigned points found
                {pointSearchTerm ? ` matching "${pointSearchTerm}"` : ""}
                {selectedAssignPointProject
                  ? ` in project "${selectedAssignPointProject.label}"`
                  : ""}
              </small>
            )}
        </FormGroup>

        <FormGroup>
          <Label for="assignPointDisplayName">Display Name</Label>
          <Input
            id="assignPointDisplayName"
            type="text"
            placeholder="Enter display name for this point..."
            value={assignPointDisplayName}
            onChange={(e) => setAssignPointDisplayName(e.target.value)}
            disabled={assigningPoint}
          />
          <small className="text-muted">
            Custom display name for this point on the device (required)
          </small>
        </FormGroup>

        <FormGroup>
          <Label>Icon</Label>
          <MdiIconPicker
            value={assignPointIcon}
            onChange={(val) => setAssignPointIcon(val)}
            disabled={assigningPoint}
          />
          <small className="text-muted">
            Search from Material Design Icons (MDI) library (required)
          </small>
        </FormGroup>

        <FormGroup>
          <Label for="assignPointType">
            Point Type <span className="text-danger">*</span>
          </Label>
          <Input
              id="assignPointType"
              type="select"
              value={assignPointTypeId}
              onChange={(e) => setAssignPointTypeId(e.target.value)}
              disabled={assigningPoint || loadingPointTypes}
              invalid={!assignPointTypeId}
          >
            <option value="" disabled>
              {loadingPointTypes ? "Loading point types..." : "— Select point type (required) —"}
            </option>
            {pointTypes.map((pt) => (
                <option key={pt.id} value={pt.id}>
                  {pt.name}{pt.description ? ` — ${pt.description}` : ""}
                </option>
            ))}
          </Input>
        </FormGroup>

        <div className="alert alert-info" style={{ fontSize: "0.85rem", padding: "8px 12px" }}>
          <strong>Note:</strong> The selected point will be assigned to device "{device?.name}"
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={assigningPoint}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleConfirmAssignPoint}
          disabled={
            assigningPoint ||
            loadingAvailablePoints ||
            !selectedPointId ||
            !assignPointDisplayName.trim() ||
            !assignPointIcon.trim()
          }
        >
          {assigningPoint ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Assigning...
            </>
          ) : (
            <>
              <Plus size={14} className="me-1" />
              Assign Point
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AssignPointToDeviceModal;
